import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  territoriesApi, inscriptionsApi, authApi,
  uploadTerritoryAvatar, partnersApi, rankingsApi,
  newsApi, territoryExtrasApi, resourcesApi, announcementsApi,
} from '../services/api';
import PhotoUpload from '../components/PhotoUpload';

// ─── Stable primitives (outside components to prevent remounting) ─────────────
const labelCss = {
  display: 'block', fontFamily: 'Space Mono,monospace', fontSize: 10,
  letterSpacing: '.08em', textTransform: 'uppercase',
  color: 'rgba(255,255,255,.45)', marginBottom: 6,
};
const inputCss = {
  width: '100%', padding: '11px 14px',
  border: '1px solid rgba(255,255,255,.15)', borderRadius: 4,
  fontFamily: 'Familjen Grotesk,sans-serif', fontSize: 15, outline: 'none',
  background: 'rgba(255,255,255,.07)', color: '#fff', transition: 'border-color .2s',
};
const textareaCss = { ...inputCss, resize: 'vertical' };

const API_BASE = process.env.REACT_APP_API_BASE || '';

/** Resolve a relative URL returned by the backend to an absolute one. */
function absUrl(url) {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('blob:')) return url;
  return `${API_BASE}${url}`;
}

function Field({ label, children }) {
  return <div style={{ marginBottom: 16 }}><label style={labelCss}>{label}</label>{children}</div>;
}
function StyledInput(props) {
  return (
    <input
      {...props}
      style={{ ...inputCss, ...props.style }}
      onFocus={e => e.target.style.borderColor = 'rgba(217,79,0,.7)'}
      onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,.15)'}
    />
  );
}
function StyledTextarea(props) {
  return (
    <textarea
      {...props}
      style={{ ...textareaCss, ...props.style }}
      onFocus={e => e.target.style.borderColor = 'rgba(217,79,0,.7)'}
      onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,.15)'}
    />
  );
}
function SaveBtn({ onClick, loading, label = 'Enregistrer les modifications →' }) {
  return (
    <button
      onClick={onClick} disabled={loading}
      style={{
        padding: '12px 28px', background: 'var(--radar-orange, #D94F00)',
        color: '#fff', border: 'none', borderRadius: 4,
        fontFamily: 'Space Mono,monospace', fontSize: 12,
        cursor: loading ? 'wait' : 'pointer',
        letterSpacing: '.04em', transition: 'opacity .2s, transform .1s',
        opacity: loading ? .7 : 1,
      }}
      onMouseEnter={e => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => e.currentTarget.style.transform = ''}
    >
      {loading ? '⏳ Enregistrement...' : label}
    </button>
  );
}
function Grid2({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>{children}</div>; }
function Grid3({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>{children}</div>; }
function SectionTitle({ children }) {
  return (
    <div style={{
      fontFamily: 'Space Mono,monospace', fontSize: 10,
      color: 'rgba(217,79,0,.8)', textTransform: 'uppercase',
      letterSpacing: '.15em', marginBottom: 20, paddingBottom: 8,
      borderBottom: '1px solid rgba(255,255,255,.06)',
    }}>{children}</div>
  );
}
function Toast({ msg, type = 'success' }) {
  if (!msg) return null;
  return (
    <div style={{
      position: 'fixed', top: 80, right: 24,
      background: type === 'error' ? '#C62828' : '#2E7D32',
      color: '#fff', padding: '14px 22px', borderRadius: 6,
      zIndex: 1000, fontFamily: 'Space Mono,monospace', fontSize: 12,
      maxWidth: 360, boxShadow: '0 8px 32px rgba(0,0,0,.4)',
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <span style={{ fontSize: 16 }}>{type === 'error' ? '⚠' : '✓'}</span> {msg}
    </div>
  );
}

// ─── Helper: upload avatar for user profile ───────────────────────────────────
async function uploadUserAvatar(file) {
  const fd = new FormData();
  fd.append('avatar', file);
  const res = await fetch(`${API_BASE}/api/auth/avatar`, {
    method : 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('radar_token')}` },
    body   : fd,
  });
  if (!res.ok) throw new Error('Erreur upload avatar');
  return res.json(); // { avatarUrl }
}

// ─── ProfileTab ───────────────────────────────────────────────────────────────
function ProfileTab({ user, updateProfile, showToast }) {
  const [name,      setName]      = useState(user.name  || '');
  const [phone,     setPhone]     = useState(user.phone || '');
  const [bio,       setBio]       = useState(user.bio   || '');
  const [curPw,     setCurPw]     = useState('');
  const [newPw,     setNewPw]     = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving,    setSaving]    = useState(false);
  const [savingPw,  setSavingPw]  = useState(false);
  const [avatarFile,    setAvatarFile]    = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(
    absUrl(user.avatar || user.avatarUrl || ''),
  );
  const avatarRef = useRef();

  const save = async () => {
    setSaving(true);
    try {
      // 1. Upload avatar first if one is selected
      if (avatarFile) {
        const data = await uploadUserAvatar(avatarFile);
        if (data.avatarUrl) setAvatarPreview(absUrl(data.avatarUrl));
        setAvatarFile(null);
      }
      // 2. Update profile fields
      await updateProfile({ name, phone, bio });
      showToast('Profil mis à jour ✓');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const changePw = async () => {
    if (newPw !== confirmPw) return showToast('Les mots de passe ne correspondent pas', 'error');
    if (newPw.length < 8)    return showToast('8 caractères minimum', 'error');
    setSavingPw(true);
    try {
      await authApi.changePassword(curPw, newPw);
      setCurPw(''); setNewPw(''); setConfirmPw('');
      showToast('Mot de passe modifié');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSavingPw(false);
    }
  };

  const handleAvatarChange = e => {
    const f = e.target.files[0];
    if (!f) return;
    setAvatarFile(f);
    setAvatarPreview(URL.createObjectURL(f));
  };

  return (
    <div style={{ maxWidth: 540 }}>
      {/* Avatar card */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 20, marginBottom: 36,
        padding: 24, background: 'rgba(255,255,255,.04)',
        borderRadius: 10, border: '1px solid rgba(255,255,255,.08)',
      }}>
        <div
          onClick={() => avatarRef.current?.click()}
          style={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}
        >
          <div style={{
            width: 80, height: 80, borderRadius: '50%', overflow: 'hidden',
            background: 'linear-gradient(135deg,var(--radar-orange, #D94F00),#c94420)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, color: '#fff', fontWeight: 700,
            border: '3px solid rgba(255,255,255,.15)',
          }}>
            {avatarPreview
              ? <img src={avatarPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span>{user.name?.charAt(0)?.toUpperCase() || 'O'}</span>}
          </div>
          <div style={{
            position: 'absolute', bottom: 0, right: 0,
            width: 24, height: 24, borderRadius: '50%',
            background: 'var(--radar-orange, #D94F00)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, border: '2px solid #1A0A00',
          }}>📷</div>
          <input
            ref={avatarRef} type="file" accept="image/*"
            style={{ display: 'none' }}
            onChange={handleAvatarChange}
          />
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 20, color: '#fff' }}>{user.name}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,.45)', marginTop: 3 }}>Organisateur·ice</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,.3)', fontFamily: 'Space Mono,monospace', marginTop: 4 }}>{user.email}</div>
          <button
            onClick={() => avatarRef.current?.click()}
            style={{
              marginTop: 8, padding: '5px 12px', background: 'none',
              border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.55)',
              borderRadius: 3, fontFamily: 'Space Mono,monospace', fontSize: 9, cursor: 'pointer',
              letterSpacing: '.05em',
            }}
          >
            {avatarPreview ? '🔄 Changer la photo' : '+ Ajouter une photo de profil'}
          </button>
        </div>
      </div>

      <SectionTitle>Informations personnelles</SectionTitle>
      <Field label="Nom complet"><StyledInput value={name}  onChange={e => setName(e.target.value)}  /></Field>
      <Field label="Téléphone">  <StyledInput value={phone} onChange={e => setPhone(e.target.value)} /></Field>
      <Field label="Bio">        <StyledTextarea rows={3} value={bio} onChange={e => setBio(e.target.value)} /></Field>
      <div style={{ marginBottom: 40 }}><SaveBtn onClick={save} loading={saving} /></div>

      <SectionTitle>Changer le mot de passe</SectionTitle>
      <Field label="Mot de passe actuel">
        <StyledInput type="password" value={curPw} onChange={e => setCurPw(e.target.value)} />
      </Field>
      <Field label="Nouveau mot de passe">
        <StyledInput type="password" value={newPw} onChange={e => setNewPw(e.target.value)} />
      </Field>
      <Field label="Confirmer">
        <StyledInput
          type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
          style={{ borderColor: confirmPw && newPw !== confirmPw ? '#E8532A' : 'rgba(255,255,255,.15)' }}
        />
      </Field>
      <button
        onClick={changePw} disabled={savingPw}
        style={{
          padding: '12px 24px', background: 'none',
          border: '1.5px solid rgba(255,255,255,.25)',
          color: 'rgba(255,255,255,.7)', borderRadius: 4,
          fontFamily: 'Space Mono,monospace', fontSize: 11, cursor: 'pointer',
        }}
      >
        {savingPw ? '...' : 'Changer le mot de passe'}
      </button>

      {/* RGPD */}
      <div style={{
        marginTop: 48, padding: 24, background: 'rgba(255,255,255,.03)',
        borderRadius: 10, border: '1px solid rgba(255,255,255,.07)',
      }}>
        <div style={{
          fontFamily: 'Space Mono,monospace', fontSize: 10,
          color: 'rgba(255,107,0,.75)', textTransform: 'uppercase',
          letterSpacing: '.14em', marginBottom: 16, paddingBottom: 8,
          borderBottom: '1px solid rgba(255,255,255,.06)',
        }}>
          Mes données personnelles (RGPD)
        </div>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,.45)', lineHeight: 1.65, marginBottom: 20 }}>
          Conformément au RGPD, vous pouvez exporter ou supprimer vos données personnelles à tout moment.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={async () => {
              try {
                const res = await fetch(`${API_BASE}/api/auth/my-data`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('radar_token')}` },
                });
                const blob = await res.blob();
                const url  = URL.createObjectURL(blob);
                const a    = document.createElement('a');
                a.href = url; a.download = 'mes-donnees-radar.json'; a.click();
                URL.revokeObjectURL(url);
                showToast('Export téléchargé ✓');
              } catch { showToast('Erreur export', 'error'); }
            }}
            style={{
              padding: '10px 18px', background: 'none',
              border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.6)',
              borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 10,
              cursor: 'pointer', letterSpacing: '.04em', textTransform: 'uppercase',
            }}
          >📥 Exporter mes données</button>

          <button
            onClick={async () => {
              if (!window.confirm('Supprimer définitivement votre compte ? Cette action est irréversible.')) return;
              try {
                await fetch(`${API_BASE}/api/auth/my-account`, {
                  method : 'DELETE',
                  headers: { Authorization: `Bearer ${localStorage.getItem('radar_token')}` },
                });
                showToast('Compte supprimé');
                setTimeout(() => { localStorage.clear(); window.location.href = '/'; }, 1500);
              } catch { showToast('Erreur', 'error'); }
            }}
            style={{
              padding: '10px 18px', background: 'rgba(200,40,40,.15)',
              border: '1px solid rgba(200,40,40,.3)', color: 'rgba(244,67,54,.8)',
              borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 10,
              cursor: 'pointer', letterSpacing: '.04em', textTransform: 'uppercase',
            }}
          >🗑 Supprimer mon compte</button>
        </div>
      </div>
    </div>
  );
}

// ─── TerritoryInfoTab ─────────────────────────────────────────────────────────
function TerritoryInfoTab({ territory, onUpdated, showToast }) {
  const [tName,              setTName]              = useState(territory.name        || '');
  const [description,        setDescription]        = useState(territory.description || '');
  const [region,             setRegion]             = useState(territory.region      || '');
  const [department,         setDepartment]         = useState(territory.department  || '');
  const [contact,            setContact]            = useState(territory.contact     || '');
  const [phone,              setPhone]              = useState(territory.phone       || '');
  const [website,            setWebsite]            = useState(territory.website     || '');
  const [instagram,          setInstagram]          = useState(territory.instagramHandle   || '');
  const [nextEvent,          setNextEvent]          = useState(territory.nextEvent   ? territory.nextEvent.split('T')[0]   : '');
  const [color,              setColor]              = useState(territory.color       || '#E8532A');
  const [mapUrl,             setMapUrl]             = useState(territory.mapUrl      || '');
  const [videoUrl,           setVideoUrl]           = useState(territory.videoUrl    || '');
  const [players,            setPlayers]            = useState(territory.players     || 0);
  const [teams,              setTeams]              = useState(territory.teams       || 0);
  const [periodStart,        setPeriodStart]        = useState(territory.periodStart ? territory.periodStart.split('T')[0] : '');
  const [periodEnd,          setPeriodEnd]          = useState(territory.periodEnd   ? territory.periodEnd.split('T')[0]   : '');
  const [instagramIframeSrc, setInstagramIframeSrc] = useState(territory.instagramIframeSrc || '');

  const [avatarFile,    setAvatarFile]    = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(absUrl(territory.avatarUrl || ''));

  const [heroFile,    setHeroFile]    = useState(null);
  const [heroPreview, setHeroPreview] = useState(absUrl(territory.heroImageUrl || ''));

  const [saving, setSaving] = useState(false);
  const avatarRef = useRef();
  const heroRef   = useRef();

  const save = async () => {
    setSaving(true);
    try {
      // 1. Save text fields
      const payload = {
        name: tName, description, region, department, contact, phone,
        website, instagramHandle: instagram, nextEvent, color, mapUrl,
        videoUrl, players: +players, teams: +teams,
        periodStart, periodEnd, instagramIframeSrc,
      };
      const updated = await territoriesApi.update(territory.id, payload);

      // 2. Upload avatar if changed
      if (avatarFile) {
        const fd  = new FormData();
        fd.append('avatar', avatarFile);
        const res = await uploadTerritoryAvatar(territory.id, fd);
        updated.avatarUrl = res.avatarUrl;
        setAvatarPreview(absUrl(res.avatarUrl));
        setAvatarFile(null);
      }

      // 3. Upload hero image if changed
      if (heroFile) {
        const fd2 = new FormData();
        fd2.append('hero', heroFile);
        const res2 = await territoriesApi.uploadHero(territory.id, fd2);
        updated.heroImageUrl = res2.heroImageUrl;
        setHeroPreview(absUrl(res2.heroImageUrl));
        setHeroFile(null);
      }

      onUpdated(updated);
      showToast('Territoire mis à jour ✓');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Build YouTube/Vimeo embed src for dashboard preview
  const embedSrc = (() => {
    if (!videoUrl) return '';
    const s = videoUrl.trim();
    try {
      if (s.includes('/embed/') || s.includes('player.vimeo.com')) return s;
      if (s.includes('youtube.com/watch')) {
        const u = new URL(s.startsWith('http') ? s : `https://${s}`);
        const v = u.searchParams.get('v');
        if (v) return `https://www.youtube.com/embed/${v}?rel=0&enablejsapi=1`;
      }
      if (s.includes('youtu.be/')) {
        const id = s.split('youtu.be/')[1].split(/[?&]/)[0];
        if (id) return `https://www.youtube.com/embed/${id}?rel=0&enablejsapi=1`;
      }
      if (s.includes('youtube.com/shorts/')) {
        const id = s.split('/shorts/')[1].split(/[?&]/)[0];
        if (id) return `https://www.youtube.com/embed/${id}?rel=0&enablejsapi=1`;
      }
      if (s.includes('youtube.com/live/')) {
        const id = s.split('youtube.com/live/')[1].split(/[?&]/)[0];
        if (id) return `https://www.youtube.com/embed/${id}?rel=0&enablejsapi=1`;
      }
      if (s.includes('vimeo.com/')) {
        const id = s.split('vimeo.com/')[1].split(/[?/]/)[0];
        if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
      }
    } catch { /* invalid URL */ }
    return s;
  })();

  return (
    <div style={{ maxWidth: 700 }}>
      {/* Avatar */}
      <div style={{
        marginBottom: 32, padding: 24,
        background: 'rgba(255,255,255,.04)', borderRadius: 10,
        border: '1px solid rgba(255,255,255,.07)',
      }}>
        <SectionTitle>Avatar du territoire</SectionTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div
            onClick={() => avatarRef.current?.click()}
            style={{
              width: 80, height: 80, borderRadius: '50%', overflow: 'hidden',
              border: `3px solid ${color}`, background: 'rgba(255,255,255,.05)',
              flexShrink: 0, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {avatarPreview
              ? <img src={avatarPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 28, opacity: .3 }}>📸</span>}
          </div>
          <div>
            <button
              onClick={() => avatarRef.current?.click()}
              style={{
                padding: '8px 18px', background: 'none',
                border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.6)',
                borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 10, cursor: 'pointer',
              }}
            >
              {avatarPreview ? '🔄 Changer' : '+ Ajouter une photo'}
            </button>
            <input
              ref={avatarRef} type="file" accept="image/*"
              style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files[0];
                if (f) { setAvatarFile(f); setAvatarPreview(URL.createObjectURL(f)); }
              }}
            />
          </div>
        </div>
      </div>

      {/* Hero background image */}
      <div style={{
        marginBottom: 32, padding: 24,
        background: 'rgba(255,255,255,.04)', borderRadius: 10,
        border: '1px solid rgba(255,255,255,.07)',
      }}>
        <SectionTitle>Image de fond de la section Accueil</SectionTitle>
        <p style={{ color: 'rgba(255,255,255,.45)', fontSize: 13, marginBottom: 14 }}>
          Cette image apparaît en arrière-plan de la section "Accueil" du territoire public.
        </p>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div
            onClick={() => heroRef.current?.click()}
            style={{
              width: 220, height: 110, borderRadius: 8, overflow: 'hidden',
              background: 'rgba(255,255,255,.06)',
              border: '2px dashed rgba(255,255,255,.2)',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, transition: 'border-color .2s',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(217,79,0,.5)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,.2)'}
          >
            {heroPreview
              ? <img src={heroPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>🖼️</div>
                  <p style={{ fontSize: 10, color: 'rgba(255,255,255,.35)', fontFamily: 'Space Mono,monospace', lineHeight: 1.4 }}>
                    Cliquer pour choisir<br />une image de fond
                  </p>
                </div>
            }
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              onClick={() => heroRef.current?.click()}
              style={{
                padding: '9px 18px', background: 'none',
                border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.65)',
                borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 10,
                cursor: 'pointer', letterSpacing: '.04em',
              }}
            >
              {heroPreview ? "🔄 Changer l'image" : '+ Choisir une image de fond'}
            </button>
            {heroPreview && (
              <button
                onClick={() => { setHeroFile(null); setHeroPreview(''); }}
                style={{
                  padding: '7px 14px', background: 'rgba(200,40,40,.15)',
                  border: '1px solid rgba(200,40,40,.3)', color: 'rgba(232,83,42,.8)',
                  borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 10, cursor: 'pointer',
                }}
              >✕ Supprimer l'image</button>
            )}
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,.25)', fontFamily: 'Space Mono,monospace', lineHeight: 1.5 }}>
              JPG, PNG, WebP · max 8 MB<br />Recommandé : 1600×900 px
            </p>
            <input
              ref={heroRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files[0];
                if (f) { setHeroFile(f); setHeroPreview(URL.createObjectURL(f)); }
              }}
            />
          </div>
        </div>
      </div>

      <SectionTitle>Informations générales</SectionTitle>
      <Grid2>
        <Field label="Nom du territoire *">
          <StyledInput value={tName} onChange={e => setTName(e.target.value)} placeholder="Vosges" />
        </Field>
        <Field label="Couleur">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input type="color" value={color} onChange={e => setColor(e.target.value)}
              style={{ width: 46, height: 42, border: 'none', cursor: 'pointer', borderRadius: 4, background: 'none' }} />
            <StyledInput value={color} onChange={e => setColor(e.target.value)} style={{ flex: 1 }} />
          </div>
        </Field>
      </Grid2>
      <Field label="Description">
        <StyledTextarea rows={3} value={description} onChange={e => setDescription(e.target.value)} placeholder="Courte description du territoire..." />
      </Field>
      <Grid2>
        <Field label="Région">    <StyledInput value={region}     onChange={e => setRegion(e.target.value)}     placeholder="Grand Est"  /></Field>
        <Field label="Département"><StyledInput value={department} onChange={e => setDepartment(e.target.value)} placeholder="Vosges 88" /></Field>
      </Grid2>

      <SectionTitle>Contact & réseaux sociaux</SectionTitle>
      <Grid2>
        <Field label="Email de contact *"><StyledInput type="email" value={contact}   onChange={e => setContact(e.target.value)}   /></Field>
        <Field label="Téléphone">         <StyledInput             value={phone}     onChange={e => setPhone(e.target.value)}     /></Field>
        <Field label="Site web">          <StyledInput             value={website}   onChange={e => setWebsite(e.target.value)}   placeholder="https://..." /></Field>
        <Field label="Instagram (sans @)"><StyledInput             value={instagram} onChange={e => setInstagram(e.target.value)} placeholder="radar.vosges" /></Field>
      </Grid2>

      <SectionTitle>Liens & médias</SectionTitle>
      <Field label="Lien carte externe (Google Maps, IGN…)">
        <StyledInput value={mapUrl} onChange={e => setMapUrl(e.target.value)} placeholder="https://maps.google.com/..." />
      </Field>
      <Field label="URL vidéo de présentation (YouTube, Vimeo — lien normal ou embed)">
        <StyledInput value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=..." />
      </Field>
      {embedSrc && (
        <div style={{ marginBottom: 16, borderRadius: 8, overflow: 'hidden', aspectRatio: '16/9', background: '#000' }}>
          <iframe src={embedSrc} width="100%" height="100%" frameBorder="0" allowFullScreen title="preview"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
        </div>
      )}

      <SectionTitle>Période de jeu</SectionTitle>
      <Grid2>
        <Field label="Début"><StyledInput type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} /></Field>
        <Field label="Fin">  <StyledInput type="date" value={periodEnd}   onChange={e => setPeriodEnd(e.target.value)}   /></Field>
      </Grid2>
      <Field label="Instagram iframe src">
        <StyledInput value={instagramIframeSrc} onChange={e => setInstagramIframeSrc(e.target.value)} placeholder="https://cdn.iframe.ly/..." />
      </Field>

      <SectionTitle>Statistiques & date</SectionTitle>
      <Grid3>
        <Field label="Participants">    <StyledInput type="number" value={players}   onChange={e => setPlayers(e.target.value)}   /></Field>
        <Field label="Équipes">         <StyledInput type="number" value={teams}     onChange={e => setTeams(e.target.value)}     /></Field>
        <Field label="Prochain événement"><StyledInput type="date" value={nextEvent} onChange={e => setNextEvent(e.target.value)} /></Field>
      </Grid3>

      <div style={{ marginTop: 8 }}><SaveBtn onClick={save} loading={saving} /></div>
    </div>
  );
}

// ─── ReglesTab ────────────────────────────────────────────────────────────────
function RuleItem({ index, title, content, onTitleChange, onContentChange, onRemove, onMove, isFirst, isLast }) {
  return (
    <div style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, padding: 20 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
          <button onClick={() => onMove(index, -1)} disabled={isFirst} style={{ width: 22, height: 22, background: 'none', border: '1px solid rgba(255,255,255,.15)', borderRadius: 2, cursor: 'pointer', color: 'rgba(255,255,255,.5)', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isFirst ? .3 : 1 }}>↑</button>
          <button onClick={() => onMove(index,  1)} disabled={isLast}  style={{ width: 22, height: 22, background: 'none', border: '1px solid rgba(255,255,255,.15)', borderRadius: 2, cursor: 'pointer', color: 'rgba(255,255,255,.5)', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isLast  ? .3 : 1 }}>↓</button>
        </div>
        <span style={{ fontFamily: 'Space Mono,monospace', fontSize: 10, color: 'rgba(217,79,0,.6)', minWidth: 20, flexShrink: 0 }}>{String(index + 1).padStart(2, '0')}</span>
        <input
          value={title} onChange={e => onTitleChange(index, e.target.value)}
          style={{ ...inputCss, fontWeight: 600, fontSize: 15, flex: 1 }}
          onFocus={e => e.target.style.borderColor = 'rgba(217,79,0,.7)'}
          onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,.15)'}
          placeholder="Titre de la section"
        />
        <button onClick={() => onRemove(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(217,79,0,.6)', fontSize: 16, padding: '4px 8px', flexShrink: 0 }}>✕</button>
      </div>
      <textarea
        value={content} onChange={e => onContentChange(index, e.target.value)} rows={3}
        style={{ ...textareaCss }}
        onFocus={e => e.target.style.borderColor = 'rgba(217,79,0,.7)'}
        onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,.15)'}
        placeholder="Contenu de cette règle..."
      />
    </div>
  );
}

function ReglesTab({ territory, showToast }) {
  const [rules, setRules] = useState(
    territory.rules?.length > 0
      ? territory.rules.map(r => ({ title: r.title, content: r.content }))
      : [
          { title: 'Durée',       content: '' },
          { title: 'Équipes',     content: '' },
          { title: 'Catégories',  content: '' },
          { title: 'Équipement',  content: '' },
          { title: 'Classement',  content: '' },
        ],
  );
  const [saving, setSaving] = useState(false);

  const setTitle   = useCallback((i, val) => setRules(prev => prev.map((r, ri) => ri === i ? { ...r, title:   val } : r)), []);
  const setContent = useCallback((i, val) => setRules(prev => prev.map((r, ri) => ri === i ? { ...r, content: val } : r)), []);
  const remove     = useCallback(i => setRules(prev => prev.filter((_, ri) => ri !== i)), []);
  const move       = useCallback((i, dir) => setRules(prev => {
    const arr = [...prev]; const t = i + dir;
    if (t < 0 || t >= arr.length) return arr;
    [arr[i], arr[t]] = [arr[t], arr[i]];
    return arr;
  }), []);

  const save = async () => {
    setSaving(true);
    try {
      await territoriesApi.updateRules(territory.id, rules.map((r, i) => ({ ...r, order: i })));
      showToast('Règles mises à jour ✓');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ marginBottom: 24, padding: '12px 16px', background: 'rgba(217,79,0,.1)', borderRadius: 6, border: '1px solid rgba(217,79,0,.2)' }}>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,.6)' }}>💡 Ces règles apparaissent dans l'onglet "Règles" de votre territoire.</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 24 }}>
        {rules.map((rule, i) => (
          <RuleItem
            key={i} index={i} title={rule.title} content={rule.content}
            onTitleChange={setTitle} onContentChange={setContent}
            onRemove={remove} onMove={move}
            isFirst={i === 0} isLast={i === rules.length - 1}
          />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={() => setRules(prev => [...prev, { title: 'Nouvelle règle', content: '' }])}
          style={{ padding: '10px 20px', background: 'none', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.7)', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 11, cursor: 'pointer' }}
        >+ Ajouter</button>
        <SaveBtn onClick={save} loading={saving} />
      </div>
    </div>
  );
}

// ─── GalerieTab ───────────────────────────────────────────────────────────────
function GalerieTab({ territory, showToast }) {
  const [gallery,      setGallery]      = useState(territory.gallery || []);
  const [videoUrl,     setVideoUrl]     = useState('');
  const [addingVideo,  setAddingVideo]  = useState(false);

  const handleUploaded = imgs => {
    setGallery(prev => [...prev, ...imgs]);
    showToast(`${imgs.length} photo(s) ajoutée(s) ✓`);
  };

  /** Add a video link to the gallery via a dedicated video endpoint */
  const addVideo = async () => {
    if (!videoUrl.trim()) return;
    setAddingVideo(true);
    try {
      // Send as JSON — many backends expect this for URL-only requests
      const res = await fetch(`${API_BASE}/api/territories/${territory.id}/gallery/video`, {
        method : 'POST',
        headers: {
          'Content-Type' : 'application/json',
          Authorization  : `Bearer ${localStorage.getItem('radar_token')}`,
        },
        body: JSON.stringify({ videoUrl: videoUrl.trim() }),
      });
      if (!res.ok) {
        // Fallback: try FormData approach if JSON endpoint doesn't exist
        const fd = new FormData();
        fd.append('videoUrl', videoUrl.trim());
        const r = await territoriesApi.uploadPhotos(territory.id, fd);
        setGallery(prev => [...prev, ...(r.images || [])]);
      } else {
        const data = await res.json();
        setGallery(prev => [...prev, ...(data.images || [data.item || data])]);
      }
      setVideoUrl('');
      showToast('Vidéo ajoutée ✓');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setAddingVideo(false);
    }
  };

  const deleteItem = async id => {
    if (!window.confirm('Supprimer ?')) return;
    try {
      await territoriesApi.deletePhoto(territory.id, id);
      setGallery(prev => prev.filter(img => img._id !== id));
      showToast('Supprimé');
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
        <div style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 10, padding: 22 }}>
          <SectionTitle>📸 Ajouter des photos</SectionTitle>
          <PhotoUpload territoryId={territory.id} onUploaded={handleUploaded} dark />
        </div>
        <div style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 10, padding: 22 }}>
          <SectionTitle>🎬 Ajouter une vidéo</SectionTitle>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,.4)', marginBottom: 14 }}>YouTube, Vimeo ou lien .mp4</p>
          <input
            value={videoUrl} onChange={e => setVideoUrl(e.target.value)}
            style={inputCss} placeholder="https://www.youtube.com/watch?v=..."
            onFocus={e => e.target.style.borderColor = 'rgba(217,79,0,.7)'}
            onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,.15)'}
          />
          <button
            onClick={addVideo} disabled={addingVideo || !videoUrl.trim()}
            style={{
              marginTop: 12, padding: '10px 20px',
              background: 'var(--radar-orange,#D94F00)', color: '#fff',
              border: 'none', borderRadius: 4, fontFamily: 'Space Mono,monospace',
              fontSize: 11, cursor: 'pointer',
              opacity: addingVideo || !videoUrl.trim() ? .5 : 1,
            }}
          >{addingVideo ? '...' : '+ Ajouter la vidéo'}</button>
        </div>
      </div>

      <SectionTitle>Galerie ({gallery.length} éléments)</SectionTitle>
      {gallery.length === 0
        ? <p style={{ color: 'rgba(255,255,255,.3)', fontSize: 14, padding: '32px 0', textAlign: 'center' }}>Aucun élément.</p>
        : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: 12 }}>
            {gallery.map(item => (
              <div key={item._id || item.url} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', aspectRatio: '4/3', background: 'rgba(255,255,255,.06)' }}>
                {item.type === 'video'
                  ? <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <span style={{ fontSize: 28 }}>🎬</span>
                      <p style={{ fontSize: 9, color: 'rgba(255,255,255,.3)', textAlign: 'center', wordBreak: 'break-all', fontFamily: 'Space Mono,monospace' }}>{(item.url || '').substring(0, 40)}...</p>
                    </div>
                  : <img src={item.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                }
                <button
                  onClick={() => deleteItem(item._id)}
                  style={{ position: 'absolute', top: 6, right: 6, width: 26, height: 26, borderRadius: '50%', background: 'rgba(200,40,40,.85)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13 }}
                >✕</button>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}

// ─── InscriptionsTab ─────────────────────────────────────────────────────────
function InscriptionsTab({ territory, showToast }) {
  const [inscriptions, setInscriptions] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const SC = {
    en_attente   : { bg: 'rgba(249,168,37,.15)', c: '#F9A825' },
    confirmee    : { bg: 'rgba(76,175,80,.15)',  c: '#4CAF50' },
    annulee      : { bg: 'rgba(244,67,54,.15)',  c: '#f44336' },
    liste_attente: { bg: 'rgba(33,150,243,.15)', c: '#2196F3' },
  };
  const SL = { en_attente: 'En attente', confirmee: 'Confirmée', annulee: 'Annulée', liste_attente: "Liste d'attente" };

 useEffect(() => {
    // ✅ CORRIGÉ : On force l'utilisation de _id pour MongoDB
    const targetId = territory._id || territory.id;
    
    inscriptionsApi.list(targetId)
      .then(setInscriptions)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [territory]); // <-- Mettez juste territory ici

  const update = async (id, status) => {
    try {
      await inscriptionsApi.updateStatus(territory.id, id, status);
      setInscriptions(prev => prev.map(i => i._id === id ? { ...i, status } : i));
      showToast(`Inscription ${status === 'confirmee' ? 'confirmée' : 'annulée'}`);
    } catch (e) { showToast(e.message, 'error'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <SectionTitle>{inscriptions.length} inscription{inscriptions.length !== 1 ? 's' : ''}</SectionTitle>
        <button
          onClick={() => inscriptionsApi.exportCSV(territory.id).then(() => showToast('CSV téléchargé !'))}
          style={{ padding: '8px 16px', background: 'none', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.5)', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 10, cursor: 'pointer' }}
        >⬇ Export CSV</button>
      </div>
      {loading
        ? <p style={{ color: 'rgba(255,255,255,.35)' }}>Chargement...</p>
        : inscriptions.length === 0
          ? <p style={{ color: 'rgba(255,255,255,.3)', textAlign: 'center', padding: '40px 0' }}>Aucune inscription.</p>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {inscriptions.map(ins => {
                const s = SC[ins.status] || SC.en_attente;
                return (
                  <div key={ins._id} style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 8, padding: '14px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>{ins.teamName}</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', marginTop: 3 }}>
                          {ins.responsableNom} · <a href={`mailto:${ins.responsableEmail}`} style={{ color: '#E8532A' }}>{ins.responsableEmail}</a>
                        </div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,.3)', marginTop: 2 }}>
                          {(ins.members || []).map(m => m.nom).join(', ')} · {ins.formula}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <span style={{ padding: '4px 10px', background: s.bg, color: s.c, borderRadius: 3, fontSize: 10, fontFamily: 'Space Mono,monospace' }}>{SL[ins.status]}</span>
                        {ins.status === 'en_attente' && (<>
                          <button onClick={() => update(ins._id, 'confirmee')} style={{ padding: '5px 12px', background: 'rgba(76,175,80,.2)', color: '#4CAF50', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 11, fontFamily: 'Space Mono,monospace' }}>✓ Confirmer</button>
                          <button onClick={() => update(ins._id, 'annulee')}   style={{ padding: '5px 12px', background: 'rgba(244,67,54,.2)', color: '#f44336', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 11, fontFamily: 'Space Mono,monospace' }}>✕ Annuler</button>
                        </>)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
      }
    </div>
  );
}

// ─── LieuxTab ─────────────────────────────────────────────────────────────────
function LieuxTab({ territory, showToast }) {
  const initialPlaces = (territory.places || []).map(p => ({
    ...p,
    details: p.details || p.description || p.detail || p.info || '',
  }));
  const [places,      setPlaces]      = useState(initialPlaces);
  const [gogocartoUrl,setGogocartoUrl]= useState(territory.gogocartoUrl || '');
  const [saving,      setSaving]      = useState(false);
  const csvRef = useRef();
  const blank  = { name: '', address: '', email: '', details: '', phone: '' };

  const importCSV = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const text  = ev.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { showToast('Fichier CSV invalide', 'error'); return; }
      const sep = lines[0].includes(';') ? ';' : ',';
      const parseLine = line => {
        const cols = []; let cur = ''; let inQ = false;
        for (const ch of line) {
          if (ch === '"') inQ = !inQ;
          else if (ch === sep && !inQ) { cols.push(cur); cur = ''; }
          else cur += ch;
        }
        cols.push(cur);
        return cols.map(c => c.trim().replace(/^["']|["']$/g, ''));
      };
      const headers = parseLine(lines[0]).map(h =>
        h.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/['"]/g, ''),
      );
      const getIdx = (...kws) => { for (const k of kws) { const i = headers.findIndex(h => h === k || h.includes(k)); if (i !== -1) return i; } return -1; };
      let iName    = getIdx('nom', 'name', 'lieu');
      let iAddress = getIdx('adress', 'lieu');
      let iEmail   = getIdx('email', 'mail', 'courriel');
      let iDetails = getIdx('detail', 'info', 'desc');
      let iPhone   = getIdx('tel', 'phone', 'contact', 'mobile');
      if (iName === -1) iName = 0;
      const imported = lines.slice(1).map((line, i) => {
        const cols = parseLine(line);
        const det  = iDetails > -1 ? cols[iDetails] || '' : '';
        return {
          name   : cols[iName]    || '',
          address: iAddress > -1 ? cols[iAddress] || '' : '',
          email  : iEmail   > -1 ? cols[iEmail]   || '' : '',
          details: det,
          phone  : iPhone   > -1 ? cols[iPhone]   || '' : '',
          order  : i,
        };
      }).filter(p => p.name);
      setPlaces(prev => [...prev, ...imported]);
      showToast(`${imported.length} lieu(x) importé(s) ✓`);
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const addPlace    = ()           => setPlaces(prev => [...prev, { ...blank }]);
  const updatePlace = (i, f, val) => setPlaces(prev => prev.map((p, pi) => pi === i ? { ...p, [f]: val } : p));
  const removePlace = i           => setPlaces(prev => prev.filter((_, pi) => pi !== i));
  const movePlace   = (i, dir)    => {
    const arr = [...places]; const t = i + dir;
    if (t < 0 || t >= arr.length) return;
    [arr[i], arr[t]] = [arr[t], arr[i]];
    setPlaces(arr);
  };

  const save = async () => {
    setSaving(true);
    try {
      await territoriesApi.update(territory.id, { gogocartoUrl });
      const placesToSave = places.map((p, i) => ({
        ...p, order: i,
        details    : p.details || '',
        description: p.details || '',
      }));
      await territoryExtrasApi.updatePlaces(territory.id, placesToSave);
      showToast('Lieux mis à jour ✓');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 740 }}>
      <SectionTitle>Tableau des lieux ({places.length})</SectionTitle>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => csvRef.current?.click()} style={{ padding: '8px 16px', background: 'none', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.6)', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 10, cursor: 'pointer' }}>📥 Importer CSV</button>
        <input ref={csvRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={importCSV} />
        <a href="data:text/csv;charset=utf-8,Nom;Adresse;Email;Details;Telephone%0ASalle+des+fetes;12+rue+de+la+Paix;contact@exemple.fr;Grande+salle;+33+6+12+34"
          download="modele-lieux.csv"
          style={{ padding: '8px 16px', background: 'none', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.4)', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 10, cursor: 'pointer' }}>
          ⬇ Modèle CSV
        </a>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        {places.map((place, i) => (
          <div key={i} style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontFamily: 'Space Mono,monospace', fontSize: 10, color: 'rgba(255,255,255,.3)' }}>LIEU {String(i + 1).padStart(2, '0')}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => movePlace(i, -1)} disabled={i === 0}               style={{ background: 'none', border: '1px solid rgba(255,255,255,.15)', borderRadius: 2, width: 24, height: 24, cursor: 'pointer', color: 'rgba(255,255,255,.5)', fontSize: 11, opacity: i === 0 ? .3 : 1 }}>↑</button>
                <button onClick={() => movePlace(i,  1)} disabled={i === places.length - 1} style={{ background: 'none', border: '1px solid rgba(255,255,255,.15)', borderRadius: 2, width: 24, height: 24, cursor: 'pointer', color: 'rgba(255,255,255,.5)', fontSize: 11, opacity: i === places.length - 1 ? .3 : 1 }}>↓</button>
                <button onClick={() => removePlace(i)} style={{ background: 'rgba(200,40,40,.15)', border: 'none', borderRadius: 2, width: 24, height: 24, cursor: 'pointer', color: 'rgba(232,83,42,.8)', fontSize: 13 }}>✕</button>
              </div>
            </div>
            <Grid2>
              <Field label="Nom *">   <StyledInput value={place.name}    onChange={e => updatePlace(i, 'name',    e.target.value)} /></Field>
              <Field label="Adresse"> <StyledInput value={place.address} onChange={e => updatePlace(i, 'address', e.target.value)} /></Field>
            </Grid2>
            <Grid2>
              <Field label="Email">   <StyledInput type="email" value={place.email} onChange={e => updatePlace(i, 'email', e.target.value)} /></Field>
              <Field label="Téléphone"><StyledInput value={place.phone}  onChange={e => updatePlace(i, 'phone',  e.target.value)} /></Field>
            </Grid2>
            <Field label="Détail du lieu">
              <StyledTextarea
                rows={2}
                value={place.details || place.description || place.detail || place.info || ''}
                onChange={e => updatePlace(i, 'details', e.target.value)}
                placeholder="Ex: Parking à l'arrière du bâtiment"
              />
            </Field>
          </div>
        ))}
        <button onClick={addPlace} style={{ padding: 12, background: 'none', border: '1px dashed rgba(255,255,255,.18)', borderRadius: 8, color: 'rgba(255,255,255,.5)', cursor: 'pointer', fontFamily: 'Space Mono,monospace', fontSize: 11 }}>+ Ajouter un lieu</button>
      </div>
      <SaveBtn onClick={save} loading={saving} />
    </div>
  );
}

// ─── PartenairesTab ───────────────────────────────────────────────────────────
function PartenairesTab({ territory, showToast }) {
  const [partners, setPartners] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [form,     setForm]     = useState({ name: '', website: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [adding,   setAdding]   = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    partnersApi.listByTerritory(territory.id).then(setPartners).catch(console.error).finally(() => setLoading(false));
  }, [territory.id]);

  const add = async () => {
    if (!form.name) return showToast('Nom requis', 'error');
    setAdding(true);
    try {
      const fd = new FormData();
      if (logoFile) fd.append('logo', logoFile);
      fd.append('data', JSON.stringify({ ...form, territorySlug: territory.id }));
      const p = await partnersApi.create(fd);
      setPartners(prev => [...prev, p]);
      setForm({ name: '', website: '' }); setLogoFile(null);
      showToast('Partenaire ajouté ✓');
    } catch (e) { showToast(e.message, 'error'); }
    finally { setAdding(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    try { await partnersApi.delete(id); setPartners(prev => prev.filter(p => p._id !== id)); showToast('Supprimé'); }
    catch (e) { showToast(e.message, 'error'); }
  };

  if (loading) return <p style={{ color: 'rgba(255,255,255,.35)' }}>Chargement...</p>;

  return (
    <div style={{ maxWidth: 640 }}>
      <SectionTitle>Partenaires actuels ({partners.length})</SectionTitle>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 32 }}>
        {partners.length === 0 && <p style={{ color: 'rgba(255,255,255,.3)', fontSize: 13 }}>Aucun partenaire.</p>}
        {partners.map(p => (
          <div key={p._id} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            {p.logoUrl
              ? <img src={absUrl(p.logoUrl)} alt={p.name} style={{ height: 32, maxWidth: 80, objectFit: 'contain' }} />
              : <div style={{ padding: '4px 10px', background: 'rgba(255,255,255,.08)', borderRadius: 3, fontFamily: 'Space Mono,monospace', fontSize: 11, color: 'rgba(255,255,255,.6)' }}>{p.name}</div>
            }
            <button onClick={() => remove(p._id, p.name)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(217,79,0,.8)', fontSize: 14 }}>✕</button>
          </div>
        ))}
      </div>

      <SectionTitle>Ajouter un partenaire</SectionTitle>
      <Grid2>
        <Field label="Nom *">   <StyledInput value={form.name}    onChange={e => setForm(f => ({ ...f, name:    e.target.value }))} /></Field>
        <Field label="Site web"><StyledInput value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://..." /></Field>
      </Grid2>
      <Field label="Logo">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={() => fileRef.current?.click()} style={{ padding: '9px 16px', background: 'none', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.6)', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 10, cursor: 'pointer' }}>
            {logoFile ? `✓ ${logoFile.name.substring(0, 20)}` : '📎 Choisir un logo'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => {
              const f = e.target.files[0];
              if (f) setLogoFile(f);
            }}
          />
          {logoFile && <button onClick={() => setLogoFile(null)} style={{ background: 'none', border: 'none', color: 'rgba(217,79,0,.8)', cursor: 'pointer' }}>✕</button>}
        </div>
      </Field>
      <div style={{ marginTop: 16 }}>
        <SaveBtn onClick={add} loading={adding} label="Ajouter le partenaire →" />
      </div>
    </div>
  );
}

// ─── ClassementTab ────────────────────────────────────────────────────────────
function ClassementTab({ territory, showToast }) {
  const [rankings, setRankings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [form,     setForm]     = useState({ teamName: '', members: '', formula: 'decouverte', totalPoints: 0, totalTimeDisplay: '' });
  const [adding,   setAdding]   = useState(false);
  
  // ✅ États pour l'édition enrichis avec tous les champs
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm]   = useState({ teamName: '', members: '', formula: 'decouverte', totalPoints: 0, totalTimeDisplay: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const edition = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

  useEffect(() => {
    rankingsApi.list(territory.id, { edition })
      .then(r => setRankings(r.sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0))))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [territory.id]);

  const addEntry = async () => {
    if (!form.teamName) return showToast('Nom équipe requis', 'error');
    setAdding(true);
    try {
      await rankingsApi.add(territory.id, {
        teamName        : form.teamName,
        members         : form.members.split(',').map(m => m.trim()).filter(Boolean),
        formula         : form.formula,
        totalTimeDisplay: form.totalTimeDisplay,
        totalPoints     : +form.totalPoints,
        edition,
      });
      showToast('Entrée ajoutée');
      setForm({ teamName: '', members: '', formula: 'decouverte', totalPoints: 0, totalTimeDisplay: '' });
      const u = await rankingsApi.list(territory.id, { edition });
      setRankings(u.sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0)));
    } catch (e) { showToast(e.message, 'error'); }
    finally { setAdding(false); }
  };

  const del = async id => {
    if (!window.confirm('Supprimer ?')) return;
    try { await rankingsApi.delete(territory.id, id); setRankings(prev => prev.filter(r => r._id !== id)); showToast('Supprimé'); }
    catch (e) { showToast(e.message, 'error'); }
  };

  // ✅ Initialisation de l'édition avec toutes les données de l'équipe
  const startEdit = (r) => {
    setEditingId(r._id);
    setEditForm({
      teamName: r.teamName || '',
      members: (r.members || []).join(', '), // Conversion du tableau en chaîne pour l'input
      formula: r.formula || 'decouverte',
      totalPoints: r.totalPoints || 0,
      totalTimeDisplay: r.totalTimeDisplay || ''
    });
  };

  // ✅ Sauvegarde avec re-transformation des membres en tableau
  const saveEdit = async (id) => {
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        members: editForm.members.split(',').map(m => m.trim()).filter(Boolean),
        totalPoints: +editForm.totalPoints,
      };
      
      await rankingsApi.update(territory.id, id, payload);
      setRankings(prev => prev.map(r => r._id === id ? { ...r, ...payload } : r).sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0)));
      setEditingId(null);
      showToast('Classement mis à jour ✓');
    } catch (e) { 
      showToast(e.message, 'error'); 
    } finally { 
      setSavingEdit(false); 
    }
  };

  if (loading) return <p style={{ color: 'rgba(255,255,255,.35)' }}>Chargement...</p>;

  const top3         = rankings.slice(0, 3);
  const podiumOrder  = [1, 0, 2];
  const podiumHeights= [130, 170, 100];
  const color        = territory.color || '#E8532A';

  return (
    <div>
      {/* PODIUM : INCHANGÉ */}
      {top3.length >= 3 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(220px,100%),1fr))', gap: 10, maxWidth: 440, marginBottom: 36 }}>
          {podiumOrder.map((idx, i) => {
            const r = top3[idx];
            if (!r) return <div key={i} />;
            const medals = ['🥈', '🥇', '🥉'];
            return (
              <div key={i}>
                <div style={{ height: podiumHeights[i], background: i === 1 ? color : 'rgba(255,255,255,.08)', borderRadius: '6px 6px 0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', padding: 10, textAlign: 'center', border: i === 1 ? 'none' : '1px solid rgba(255,255,255,.1)' }}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{medals[i]}</div>
                  <div style={{ fontFamily: 'Space Mono,monospace', fontSize: 20, fontWeight: 700 }}>{r.totalPoints || 0}</div>
                  <div style={{ fontSize: 10, opacity: .7, textTransform: 'uppercase' }}>pts</div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4 }}>{r.teamName}</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,.04)', borderRadius: '0 0 6px 6px', padding: '8px 10px', fontSize: 10, color: 'rgba(255,255,255,.4)', textAlign: 'center', fontFamily: 'Space Mono,monospace' }}>{r.totalTimeDisplay || '—'}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* TABLEAU DES SCORES */}
      <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 8, overflow: 'hidden', marginBottom: 28 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr 85px 90px 90px 70px', padding: '10px 16px', background: 'rgba(255,255,255,.05)', fontFamily: 'Space Mono,monospace', fontSize: 9, color: 'rgba(255,255,255,.35)', textTransform: 'uppercase', letterSpacing: '.08em', gap: 6 }}>
          <span>Rang</span><span>Équipe</span><span>Formule</span><span>Temps</span><span style={{ textAlign: 'right' }}>Points</span><span></span>
        </div>
        
        {rankings.length === 0
          ? <div style={{ padding: 28, textAlign: 'center', color: 'rgba(255,255,255,.3)', fontSize: 13 }}>Aucune équipe classée.</div>
          : rankings.map((r, i) => (
            <div key={r._id || i} style={{ display: 'grid', gridTemplateColumns: '44px 1fr 85px 90px 90px 70px', padding: '13px 16px', alignItems: 'center', gap: 6, borderTop: '1px solid rgba(255,255,255,.05)', background: editingId === r._id ? 'rgba(255,255,255,.04)' : 'transparent' }}>
              <span style={{ fontFamily: 'Space Mono,monospace', fontWeight: 700, fontSize: 14, color: i === 0 ? color : i < 3 ? 'rgba(255,255,255,.6)' : 'rgba(255,255,255,.3)' }}>{i + 1}</span>
              
              {editingId === r._id ? (
                <>
                  {/* Mode Édition : Noms, Membres et Formule éditables */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input value={editForm.teamName} onChange={e => setEditForm(f => ({ ...f, teamName: e.target.value }))} placeholder="Nom" style={{ width: '100%', boxSizing: 'border-box', padding: '4px 8px', background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,.2)', color: '#fff', borderRadius: 4, fontFamily: 'Familjen Grotesk,sans-serif', fontSize: 13 }} />
                    <input value={editForm.members} onChange={e => setEditForm(f => ({ ...f, members: e.target.value }))} placeholder="Membres" style={{ width: '100%', boxSizing: 'border-box', padding: '4px 8px', background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.6)', borderRadius: 4, fontFamily: 'Familjen Grotesk,sans-serif', fontSize: 11 }} />
                  </div>
                  <select value={editForm.formula} onChange={e => setEditForm(f => ({ ...f, formula: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box', padding: '4px', background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,.2)', color: '#fff', borderRadius: 4, fontFamily: 'Familjen Grotesk,sans-serif', fontSize: 11, outline: 'none' }}>
                    <option value="decouverte">Découverte</option>
                    <option value="sport">Sport</option>
                    <option value="expert">Expert</option>
                  </select>
                  <input value={editForm.totalTimeDisplay} onChange={e => setEditForm(f => ({ ...f, totalTimeDisplay: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box', padding: '4px 8px', background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,.2)', color: '#fff', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 11 }} />
                  <input type="number" value={editForm.totalPoints} onChange={e => setEditForm(f => ({ ...f, totalPoints: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box', padding: '4px 8px', background: 'rgba(0,0,0,.2)', border: '1px solid rgba(255,255,255,.2)', color: '#fff', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 12, textAlign: 'right' }} />
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button onClick={() => saveEdit(r._id)} disabled={savingEdit} style={{ background: 'rgba(76,175,80,.2)', border: 'none', cursor: 'pointer', color: '#4CAF50', fontSize: 13, borderRadius: 3, padding: '4px 8px' }}>✓</button>
                    <button onClick={() => setEditingId(null)} style={{ background: 'rgba(244,67,54,.2)', border: 'none', cursor: 'pointer', color: '#f44336', fontSize: 13, borderRadius: 3, padding: '4px 8px' }}>✕</button>
                  </div>
                </>
              ) : (
                <>
                  {/* Mode Lecture */}
                  <div><div style={{ fontWeight: 600, fontSize: 14, color: '#fff' }}>{r.teamName}</div><div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)' }}>{(r.members || []).join(', ')}</div></div>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', textTransform: 'capitalize' }}>{r.formula}</span>
                  <span style={{ fontFamily: 'Space Mono,monospace', fontSize: 12, color: 'rgba(255,255,255,.5)' }}>{r.totalTimeDisplay || '—'}</span>
                  <span style={{ fontFamily: 'Space Mono,monospace', fontSize: 14, textAlign: 'right', fontWeight: 700, color: i === 0 ? color : '#fff' }}>{r.totalPoints || 0} <span style={{ fontSize: 9, opacity: .5 }}>pts</span></span>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button onClick={() => startEdit(r)} style={{ background: 'none', border: '1px solid rgba(255,255,255,.15)', borderRadius: 3, color: 'rgba(255,255,255,.5)', cursor: 'pointer', fontSize: 11, padding: '4px 8px' }}>✏️</button>
                    <button onClick={() => del(r._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(232,83,42,.6)', fontSize: 13, padding: '4px 8px' }}>✕</button>
                  </div>
                </>
              )}
            </div>
          ))
        }
      </div>

      {/* FORMULAIRE AJOUT : INCHANGÉ */}
      <SectionTitle>Ajouter / modifier une entrée</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <Field label="Équipe *">         <StyledInput value={form.teamName}        onChange={e => setForm(f => ({ ...f, teamName:         e.target.value }))} placeholder="Les Chamois" /></Field>
        <Field label="Membres (séparés ,)"><StyledInput value={form.members}         onChange={e => setForm(f => ({ ...f, members:          e.target.value }))} placeholder="Léa, Karim" /></Field>
        <Field label="Formule">
          <select value={form.formula} onChange={e => setForm(f => ({ ...f, formula: e.target.value }))}
            style={{ width: '100%', padding: '11px 14px', border: '1px solid rgba(255,255,255,.15)', borderRadius: 4, fontFamily: 'Familjen Grotesk,sans-serif', fontSize: 14, background: 'rgba(255,255,255,.07)', color: '#fff', outline: 'none' }}>
            <option value="decouverte">Découverte</option>
            <option value="sport">Sport</option>
            <option value="expert">Expert</option>
          </select>
        </Field>
      </div>
      <Grid2>
        <Field label="Temps"><StyledInput value={form.totalTimeDisplay} onChange={e => setForm(f => ({ ...f, totalTimeDisplay: e.target.value }))} placeholder="2h34" /></Field>
        <Field label="Points *"><StyledInput type="number" value={form.totalPoints} onChange={e => setForm(f => ({ ...f, totalPoints: e.target.value }))} /></Field>
      </Grid2>
      <SaveBtn onClick={addEntry} loading={adding} label="Ajouter l'entrée →" />
    </div>
  );
}

// ─── ArticlesTab ─────────────────────────────────────────────────────────────
function ArticlesTab({ territory, showToast }) {
  const [articles,     setArticles]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [editing,      setEditing]      = useState(null);
  const [form,         setForm]         = useState({ title: '', excerpt: '', content: '' });
  const [saving,       setSaving]       = useState(false);
  const [coverFile,    setCoverFile]    = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const fileRef = useRef();

  useEffect(() => {
    newsApi.list({ limit: 20, territory: territory.id, all: 'true' })
      .then(setArticles).catch(console.error).finally(() => setLoading(false));
  }, [territory.id]);

  const startNew = () => {
    setEditing('new');
    setForm({ title: '', excerpt: '', content: '' });
    setCoverFile(null); setCoverPreview('');
  };
  const startEdit = a => {
    setEditing(a._id);
    setForm({ title: a.title, excerpt: a.excerpt || '', content: a.content || '' });
    setCoverFile(null);
    setCoverPreview(absUrl(a.coverImage || ''));
  };

  const save = async () => {
    if (!form.title || !form.content) return showToast('Titre et contenu requis', 'error');
    setSaving(true);
    try {
      const fd = new FormData();
      if (coverFile) fd.append('coverImage', coverFile);
      fd.append('data', JSON.stringify({ ...form, isPublished: true, territorySlug: territory.id }));

      if (editing === 'new') {
        const article = await newsApi.create(fd);
        setArticles(prev => [article, ...prev]);
        showToast('Article publié dans votre territoire ✓');
      } else {
        const updated = await newsApi.update(editing, fd);
        setArticles(prev => prev.map(a => a._id === editing ? updated : a));
        showToast('Article mis à jour ✓');
      }
      setEditing(null);
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const del = async (id, title) => {
    if (!window.confirm(`Supprimer l'article "${title}" ?`)) return;
    try { await newsApi.delete(id); setArticles(prev => prev.filter(a => a._id !== id)); showToast('Article supprimé'); }
    catch (e) { showToast(e.message, 'error'); }
  };

  if (loading) return <p style={{ color: 'rgba(255,255,255,.35)' }}>Chargement...</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <p style={{ color: 'rgba(255,255,255,.6)', fontSize: 14, marginBottom: 4 }}>Articles publiés dans RADAR {territory.name}</p>
          <p style={{ color: 'rgba(255,255,255,.35)', fontSize: 12 }}>Ces articles apparaissent uniquement dans l'onglet Actualités de votre territoire.</p>
        </div>
        {editing === null && (
          <button onClick={startNew} style={{ padding: '10px 20px', background: 'var(--radar-orange,#D94F00)', color: '#fff', border: 'none', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Nouvel article
          </button>
        )}
      </div>

      {editing !== null && (
        <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 10, padding: 24, marginBottom: 28 }}>
          <Field label="Titre *"><StyledInput value={form.title}   onChange={e => setForm(f => ({ ...f, title:   e.target.value }))} placeholder="Titre de l'article..." /></Field>
          <Field label="Extrait"><StyledTextarea rows={2} value={form.excerpt}  onChange={e => setForm(f => ({ ...f, excerpt:  e.target.value }))} placeholder="Courte description..." /></Field>
          <Field label="Contenu *"><StyledTextarea rows={8} value={form.content} onChange={e => setForm(f => ({ ...f, content:  e.target.value }))} placeholder="Rédigez votre article ici..." /></Field>
          <Field label="Image de couverture">
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button onClick={() => fileRef.current?.click()} style={{ padding: '9px 14px', background: 'none', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.6)', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 10, cursor: 'pointer' }}>
                {coverFile ? `✓ ${coverFile.name.substring(0, 20)}` : '📸 Choisir une image'}
              </button>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files[0]; if (f) { setCoverFile(f); setCoverPreview(URL.createObjectURL(f)); } }}
              />
              {coverPreview && (
                <img src={coverPreview} alt="" style={{ height: 40, borderRadius: 4 }} />
              )}
            </div>
          </Field>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <SaveBtn onClick={save} loading={saving} label={editing === 'new' ? "Publier l'article →" : 'Mettre à jour →'} />
            <button onClick={() => setEditing(null)} style={{ padding: '11px 18px', background: 'none', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.6)', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 11, cursor: 'pointer' }}>Annuler</button>
          </div>
        </div>
      )}

      {editing === null && articles.length === 0
        ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', background: 'rgba(255,255,255,.03)', borderRadius: 10, border: '1px dashed rgba(255,255,255,.1)' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📰</div>
            <p style={{ color: 'rgba(255,255,255,.3)', fontSize: 14 }}>Aucun article pour l'instant.</p>
          </div>
        )
        : editing === null && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {articles.map(a => (
              <div key={a._id} style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 8, padding: '14px 18px', display: 'flex', gap: 16, alignItems: 'center' }}>
                {a.coverImage && <img src={absUrl(a.coverImage)} alt="" style={{ width: 80, height: 54, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                    <span style={{ padding: '2px 7px', background: a.isPublished ? 'rgba(76,175,80,.2)' : 'rgba(249,168,37,.15)', color: a.isPublished ? '#4CAF50' : '#F9A825', fontSize: 9, fontFamily: 'Space Mono,monospace', borderRadius: 2 }}>
                      {a.isPublished ? '✓ Publié' : 'Brouillon'}
                    </span>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#fff', marginBottom: 3 }}>{a.title}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', fontFamily: 'Space Mono,monospace' }}>
                    {new Date(a.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => startEdit(a)} style={{ padding: '5px 10px', background: 'none', border: '1px solid rgba(255,255,255,.15)', borderRadius: 3, color: 'rgba(255,255,255,.5)', cursor: 'pointer', fontSize: 11 }}>✏️</button>
                  <button onClick={() => del(a._id, a.title)} style={{ padding: '5px 10px', background: 'rgba(200,40,40,.2)', border: 'none', borderRadius: 3, color: '#f44336', cursor: 'pointer', fontSize: 11 }}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}

// ─── RessourcesTab ────────────────────────────────────────────────────────────
const CATS_R = ['Communication', 'Organisation', 'Règles', 'Sécurité', 'Autre'];
function RessourcesTab({ territory, showToast }) {
  const [resources, setResources] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState({ title: '', description: '', category: 'Organisation', format: 'PDF', size: '', icon: '📄', externalUrl: '' });
  const [file,      setFile]      = useState(null);
  const [saving,    setSaving]    = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    resourcesApi.list({ territory: territory.id })
      .then(setResources).catch(console.error).finally(() => setLoading(false));
  }, [territory.id]);

  const startNew  = () => { setEditing('new'); setForm({ title: '', description: '', category: 'Organisation', format: 'PDF', size: '', icon: '📄', externalUrl: '' }); setFile(null); };
  const startEdit = r => { setEditing(r._id); setForm({ title: r.title, description: r.description || '', category: r.category, format: r.format, size: r.size || '', icon: r.icon || '📄', externalUrl: r.externalUrl || '' }); setFile(null); };

  const save = async () => {
    if (!form.title) return showToast('Titre requis', 'error');
    setSaving(true);
    try {
      const fd = new FormData();
      if (file) fd.append('file', file);
      fd.append('data', JSON.stringify({ ...form, territorySlug: territory.id }));
      let result;
      if (editing === 'new') {
        result = await resourcesApi.create(fd);
        setResources(prev => [...prev, result]);
        showToast('Ressource ajoutée ✓');
      } else {
        result = await resourcesApi.update(editing, fd);
        setResources(prev => prev.map(r => r._id === editing ? result : r));
        showToast('Ressource mise à jour ✓');
      }
      setEditing(null);
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const del = async (id, title) => {
    if (!window.confirm(`Supprimer "${title}" ?`)) return;
    try { await resourcesApi.delete(id); setResources(prev => prev.filter(r => r._id !== id)); showToast('Supprimé'); }
    catch (e) { showToast(e.message, 'error'); }
  };

  if (loading) return <p style={{ color: 'rgba(255,255,255,.35)' }}>Chargement...</p>;
  const FMT_COLOR = { PDF: '#FF6B00', DOCX: '#1565C0', XLSX: '#2E7D32', ZIP: '#6A1B9A' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <p style={{ color: 'rgba(255,255,255,.6)', fontSize: 14, marginBottom: 4 }}>Ressources de RADAR {territory.name}</p>
          <p style={{ color: 'rgba(255,255,255,.35)', fontSize: 12 }}>Ces ressources apparaissent uniquement dans la section Ressources de votre territoire.</p>
        </div>
        {editing === null && (
          <button onClick={startNew} style={{ padding: '9px 18px', background: 'var(--radar-orange,#D94F00)', color: '#fff', border: 'none', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>+ Nouvelle ressource</button>
        )}
      </div>

      {editing !== null && (
        <div style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 10, padding: 24, marginBottom: 24 }}>
          <Field label="Titre *"><StyledInput value={form.title}       onChange={e => setForm(f => ({ ...f, title:       e.target.value }))} /></Field>
          <Field label="Description"><StyledTextarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></Field>
          <Grid2>
            <Field label="Catégorie">
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', border: '1px solid rgba(255,255,255,.15)', borderRadius: 4, background: 'rgba(255,255,255,.07)', color: '#fff', fontFamily: 'Familjen Grotesk,sans-serif', fontSize: 14, outline: 'none' }}>
                {CATS_R.map(cat => <option key={cat}>{cat}</option>)}
              </select>
            </Field>
            <Field label="Format"><StyledInput value={form.format} onChange={e => setForm(f => ({ ...f, format: e.target.value.toUpperCase() }))} placeholder="PDF" /></Field>
          </Grid2>
          <Grid2>
            <Field label="Fichier">
              <button onClick={() => fileRef.current?.click()} style={{ padding: '9px 14px', background: 'none', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.6)', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 10, cursor: 'pointer' }}>
                {file ? `✓ ${file.name.substring(0, 20)}` : '📎 Choisir un fichier'}
              </button>
              <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
            </Field>
            <Field label="Ou URL externe"><StyledInput value={form.externalUrl} onChange={e => setForm(f => ({ ...f, externalUrl: e.target.value }))} placeholder="https://..." /></Field>
          </Grid2>
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <SaveBtn onClick={save} loading={saving} label={editing === 'new' ? 'Ajouter →' : 'Mettre à jour →'} />
            <button onClick={() => setEditing(null)} style={{ padding: '11px 18px', background: 'none', border: '1px solid rgba(255,255,255,.2)', color: 'rgba(255,255,255,.6)', borderRadius: 4, fontFamily: 'Space Mono,monospace', fontSize: 11, cursor: 'pointer' }}>Annuler</button>
          </div>
        </div>
      )}

      {resources.length === 0 && editing === null
        ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', background: 'rgba(255,255,255,.03)', borderRadius: 10, border: '1px dashed rgba(255,255,255,.1)' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📁</div>
            <p style={{ color: 'rgba(255,255,255,.3)', fontSize: 14 }}>Aucune ressource pour ce territoire.</p>
          </div>
        )
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {resources.map(r => (
              <div key={r._id} style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 8, padding: '14px 18px', display: 'flex', gap: 14, alignItems: 'center' }}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>{r.icon || '📄'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#fff' }}>{r.title}</div>
                  {r.description && <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', marginTop: 2 }}>{r.description.substring(0, 60)}{r.description.length > 60 ? '…' : ''}</div>}
                </div>
                <span style={{ padding: '2px 8px', background: `${FMT_COLOR[r.format] || '#455A64'}20`, color: FMT_COLOR[r.format] || '#aaa', borderRadius: 2, fontFamily: 'Space Mono,monospace', fontSize: 9, flexShrink: 0 }}>{r.format}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => startEdit(r)} style={{ padding: '5px 10px', background: 'none', border: '1px solid rgba(255,255,255,.15)', borderRadius: 3, color: 'rgba(255,255,255,.5)', cursor: 'pointer', fontSize: 11 }}>✏️</button>
                  <button onClick={() => del(r._id, r.title)} style={{ padding: '5px 10px', background: 'rgba(200,40,40,.2)', border: 'none', borderRadius: 3, color: '#f44336', cursor: 'pointer', fontSize: 11 }}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}

// ─── AnnouncementsPanel ───────────────────────────────────────────────────────
function AnnouncementsPanel() {
  const [announcements, setAnnouncements] = useState([]);
  useEffect(() => { announcementsApi.list().then(setAnnouncements).catch(console.error); }, []);
  const unread = announcements.filter(a => !a.isRead);
  if (!unread.length) return null;
  const markRead = async id => {
    try { await announcementsApi.markRead(id); setAnnouncements(prev => prev.map(a => a._id === id ? { ...a, isRead: true } : a)); }
    catch { /* silent */ }
  };
  return (
    <div style={{ marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {unread.map(a => (
        <div key={a._id} style={{ background: 'rgba(255,107,0,.12)', border: '1px solid rgba(255,107,0,.25)', borderRadius: 8, padding: '14px 18px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>📢</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontFamily: 'Space Mono,monospace', color: 'rgba(255,107,0,.7)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>
              Message de l'admin · {new Date(a.createdAt).toLocaleDateString('fr-FR')}
            </div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,.85)', lineHeight: 1.6 }}>{a.message}</div>
          </div>
          <button onClick={() => markRead(a._id)} style={{ background: 'none', border: '1px solid rgba(255,255,255,.15)', borderRadius: 3, color: 'rgba(255,255,255,.4)', fontSize: 10, cursor: 'pointer', padding: '4px 8px', fontFamily: 'Space Mono,monospace', flexShrink: 0 }}>✓ Lu</button>
        </div>
      ))}
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function OrganisateurDashboard() {
  const { user, loading, isAuthenticated, isOrganisateur, updateProfile } = useAuth();
  const [territory,  setTerritory]  = useState(null);
  const [loadingT,   setLoadingT]   = useState(true);
  const [activeTab,  setActiveTab]  = useState('profil');
  const [toast,      setToast]      = useState({ msg: '', type: 'success' });
  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3200);
  }, []);

  useEffect(() => {
    if (!user?.territory) { setLoadingT(false); return; }
    const tid = user.territory?.id || user.territory;
    if (typeof tid === 'string') {
      territoriesApi.get(tid).then(setTerritory).catch(console.error).finally(() => setLoadingT(false));
    } else {
      setLoadingT(false);
    }
  }, [user]);

  if (loading || loadingT) return <div style={{ paddingTop: 120, textAlign: 'center', color: '#8C7A6A' }}>Chargement...</div>;
  if (!isAuthenticated)    return <Navigate to="/login" />;
  if (!isOrganisateur)     return <Navigate to="/" />;

  const color = territory?.color || '#E8532A';
  const tabs  = [
    { id: 'profil',        label: '👤 Profil' },
    ...(territory ? [
      { id: 'territoire',  label: '🗺️ Territoire' },
      { id: 'regles',      label: '📋 Règles' },
      { id: 'lieux',       label: '📍 Les lieux' },
      { id: 'galerie',     label: '📸 Galerie' },
      { id: 'partenaires', label: '🤝 Partenaires' },
      { id: 'classement',  label: '🏆 Classement' },
      { id: 'inscriptions',label: '✉️ Inscriptions' },
      { id: 'articles',    label: '📰 Articles' },
      { id: 'ressources',  label: '📁 Ressources' },
    ] : []),
  ];
  const heroBg = territory?.avatarUrl
    ? `linear-gradient(160deg,rgba(14,10,4,.92) 0%,rgba(26,18,8,.85) 100%),url(${absUrl(territory.avatarUrl)}) center/cover no-repeat`
    : 'linear-gradient(160deg,#0E0A04 0%,#1A1208 100%)';

  return (
    <div style={{ paddingTop: 'var(--nav-h, 64px)', minHeight: '100vh', background: '#1A1208' }}>
      <Toast msg={toast.msg} type={toast.type} />
      <div style={{ background: heroBg, borderBottom: '1px solid rgba(255,255,255,.06)', padding: '0 var(--px,clamp(16px,5vw,40px)) 16px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right,rgba(14,10,4,.95) 50%,rgba(14,10,4,.6))', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '50%', right: '5%', width: 200, height: 200, borderRadius: '50%', border: `1px solid ${color}22`, transform: 'translateY(-50%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative' }}>
          <div style={{ padding: '36px 0 0' }}>
            <p style={{ fontFamily: 'Space Mono,monospace', fontSize: 10, color: 'rgba(255,255,255,.3)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 10 }}>Espace organisateur</p>
            <h1 style={{ fontSize: 32, fontWeight: 700, color: '#fff', marginBottom: 6 }}>Bonjour, {user.name?.split(' ')[0]} 👋</h1>
            {territory && (
              <p style={{ color: 'rgba(255,255,255,.45)', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
                Vous gérez <strong style={{ color: 'rgba(255,255,255,.75)' }}>&nbsp;RADAR {territory.name}</strong>
              </p>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: 28, borderTop: '1px solid rgba(255,255,255,.05)', paddingTop: 12 }}>
            {tabs.map(t => (
              <button
                key={t.id} onClick={() => setActiveTab(t.id)}
                style={{
                  padding: '13px 18px', background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: 'Space Mono,monospace', fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase',
                  color          : activeTab === t.id ? color : 'rgba(255,255,255,.4)',
                  borderBottom   : `2px solid ${activeTab === t.id ? color : 'transparent'}`,
                  transition     : 'all .2s', marginBottom: -1, whiteSpace: 'nowrap',
                }}
              >{t.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(24px,5vw,48px) var(--px,clamp(16px,5vw,40px))' }}>
        <AnnouncementsPanel />
        {activeTab === 'profil'        && <ProfileTab         user={user} updateProfile={updateProfile} showToast={showToast} />}
        {activeTab === 'territoire'    && territory && <TerritoryInfoTab  territory={territory} onUpdated={setTerritory} showToast={showToast} />}
        {activeTab === 'regles'        && territory && <ReglesTab         territory={territory} showToast={showToast} />}
        {activeTab === 'lieux'         && territory && <LieuxTab          territory={territory} showToast={showToast} />}
        {activeTab === 'galerie'       && territory && <GalerieTab        territory={territory} showToast={showToast} />}
        {activeTab === 'partenaires'   && territory && <PartenairesTab    territory={territory} showToast={showToast} />}
        {activeTab === 'classement'    && territory && <ClassementTab     territory={territory} showToast={showToast} />}
        {activeTab === 'inscriptions'  && territory && <InscriptionsTab   territory={territory} showToast={showToast} />}
        {activeTab === 'articles'      && territory && <ArticlesTab       territory={territory} showToast={showToast} />}
        {activeTab === 'ressources'    && territory && <RessourcesTab     territory={territory} showToast={showToast} />}
      </div>
    </div>
  );
}